import * as React from 'react';
import kebabCase from 'lodash/kebabCase';
import {clsx} from 'clsx';

import {FilterPartsProps} from 'common/query-builder/query-entry-section/row-types/filter/parts/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {
  BooleanOperator,
  FilterOperator,
  FilterValue,
  ListQuantifier,
} from 'common/widgets/property-filter-menu/models/types';
import {
  applyFilterOperatorToFilter,
  getDurationFilterUnitLabel,
  getFilterOperatorsSections,
  getFilterOperatorSymbol,
  getListQuantifierSections,
} from 'common/query-builder/query-entry-section/row-types/filter/utils';
import {hasComputedListFilters, isEnhancedListFilter} from 'common/widgets/property-filter-menu/utils';
// eslint-disable-next-line import/no-cycle -- value component can render filter parts recursively
import {
  FilterListValueTemplate,
  FilterValueTemplate,
} from 'common/query-builder/query-entry-section/row-types/filter/types/index';
import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {Placement} from 'common/components/tooltip/types';
import {ChangeEvent} from 'common/components/select/types';
import {SectionItem} from 'common/components/items-menu/types';
import {TypecastSubitem} from 'common/query-builder/query-entry-section/components/typecast-subitem';
import {DURATION_UNIT_OPTION_SECTION_ITEMS} from 'common/query-builder/query-block/properties-menu/constants';
import {ProfileHistorySubItem} from 'common/query-builder/query-entry-section/components/profile-history-subitem';
import {SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';

// defines which filter types do not render an operator selector
// this is a temporal check until we can bring all the new filter
// UI to all filter types
const NO_OPERATOR_TYPES = new Set<PropertyType>([PropertyType.Object, PropertyType.NonProperty]);

export function FilterParts(props: FilterPartsProps) {
  const hasProperty = props.filter.hasSelectedProperty();
  const isCohortFilter = props.filter.resourceType === ResourceType.Cohort;

  const changeOperator = (operator: FilterOperator) => {
    const newFilter = applyFilterOperatorToFilter(operator, props.filter);

    props.updateEntry(newFilter);
    props.onChangeOperator?.(operator);
  };

  const changeValue = (value: FilterValue) => {
    if (props.filter.propertyType === PropertyType.Boolean) {
      // changing the operator for boolean values also handles changing the value
      changeOperator(value ? BooleanOperator.True : BooleanOperator.False);
    } else {
      props.updateEntry(props.filter.setAttribute(`filterValue`, value));
    }

    props.onChangeValue?.(value);
  };

  const showListQuantifier =
    isEnhancedListFilter(props.filter) && !props.hideListQuantifier && !hasComputedListFilters(props.filter);

  const FilterValue =
    (isEnhancedListFilter(props.filter) && FilterListValueTemplate[props.filter.propertyType]) ||
    FilterValueTemplate[props.filter.propertyType];

  return (
    <div
      className={clsx(`mp-query-entry-filter-parts`, {
        'with-quick-delete': props.withQuickDelete,
        'with-quick-rename': props.withQuickRename,
      })}
    >
      {/** enhanced list property filters define a quantifier operator for items in the list */}
      {showListQuantifier && (
        <div className='mp-query-entry-filter-parts-row'>
          <SelectReact
            elref='listFilterQuantifier'
            className='list-filter-quantifier'
            menuWidth={200}
            alwaysShowCaret={props.hasNewUI}
            theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
            tooltip={{placement: Placement.Top}}
            readonly={props.readonly}
            sections={getListQuantifierSections(props.filter)}
            onChange={({detail}: ChangeEvent<SectionItem<ListQuantifier>>) => {
              props.updateEntry(props.filter.setAttribute(`listQuantifier`, detail.item.value));
            }}
          />
        </div>
      )}

      <div className='mp-query-entry-filter-parts-row'>
        {hasProperty &&
          !isCohortFilter &&
          !NO_OPERATOR_TYPES.has(props.filter.propertyType) &&
          // newUI uses a radio-button selection specified in FilterValueTemplate for boolean values instead of a SelectEl
          !(props.hasNewUI && props.filter.propertyType === PropertyType.Boolean) && (
            <SelectReact
              elref='filterOperator'
              className={clsx({
                [`for-${props.filter.propertyType}`]: true,
                [`for-${kebabCase(props.filter.filterOperator)}-operator`]: true,
              })}
              disableSearch
              menuWidth={200}
              menuHeight={0}
              readonly={props.readonly}
              selectedOption={
                props.operatorOverrides?.find(({value}) => value === props.filter.filterOperator)?.label ||
                getFilterOperatorSymbol(props.filter)
              }
              theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
              neverShowCaret
              disableTooltip
              sections={getFilterOperatorsSections(props.filter, props.operatorOverrides)}
              onChange={(ev: ChangeEvent) => changeOperator(ev.detail.item.value[0])}
            />
          )}

        {/** render value selection only for non-cohort filters */}
        {!isCohortFilter && FilterValue && (
          <FilterValue
            filter={props.filter}
            eventNames={props.eventNames}
            dataGroupId={props.dataGroupId}
            projectId={props.projectId}
            workspaceId={props.workspaceId}
            userId={props.userId}
            readonly={props.readonly}
            hasNewUI={props.hasNewUI}
            rowActions={props.rowActions}
            hasPlaceholder={props.hasPlaceholder}
            isActive={props.isValueActive}
            onChange={(value) => changeValue(value)}
            onSelectMenuChange={(ev) => props.onValueSelectMenuChange?.(ev)}
            setActiveEntry={(state) => props.setActiveEntry?.(state)}
            updateEntry={(filter) => props.updateEntry(filter)}
            isTimePickerEnabled={props.isTimePickerEnabled}
            setTimePickerEnabled={props.setTimePickerEnabled}
          />
        )}

        {FilterValue && props.filter.supportsFilterUnit() && (
          <SelectReact
            elref='filterUnit'
            disableSearch
            menuWidth={200}
            menuHeight={0}
            readonly={props.readonly}
            selectedOption={getDurationFilterUnitLabel(props.filter)}
            theme={props.hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select}
            neverShowCaret
            disableTooltip
            className={clsx({
              [`for-${props.filter.propertyType}`]: true,
              [`for-${kebabCase(props.filter.filterOperator)}-operator`]: true,
            })}
            sections={[{items: DURATION_UNIT_OPTION_SECTION_ITEMS}]}
            onChange={(ev: ChangeEvent) =>
              props.updateEntry(props.filter.setAttribute(`filterDateUnit`, ev.detail.item.value))
            }
          />
        )}

        {props.unit && <div className='filter-value-unit'>{props.unit}</div>}
      </div>

      {props.hasNewUI && props.showTypecastControl && (
        <TypecastSubitem
          value={props.filter}
          onTypeChange={props.updateEntry}
          disabledTypes={props.disabledTypecastTypes}
          forFilter
        />
      )}

      {hasProperty && props.isProfileHistoryEnabled && props.filter.resourceType === ResourceType.User && (
        <ProfileHistorySubItem
          hasNewUI={props.hasNewUI}
          value={props.filter}
          onTypeChange={props.updateEntry}
          hideEvents={props.hideEvents}
          selectorType={SelectorTypeDefinitions.Filter}
        />
      )}
    </div>
  );
}
